import React from 'react';
import { useEffect, useState } from 'react';
import LinearProgress from '@mui/material/LinearProgress';
import { Box, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { device, properties } from '../redux/Selectors.js';
import { useSelector } from 'react-redux';

/**
 * ZEN-29183: Laedt die Kundenseite nicht innerhalb dieser Zeit (z.B. weil die
 * HTML-Ressource geloescht wurde oder eine externe Seite das Einbetten verbietet),
 * wird der Splash automatisch geschlossen, damit der WebStore nutzbar bleibt.
 */
export const PAGE_LOAD_TIMEOUT = 8000;

/**
 * Splash-Screen der React-UI.
 *
 * Zwei Betriebsarten:
 * - klassisch (pageUrl leer): geraeteabhaengiges Hintergrundbild, die Anwendung
 *   schaltet nach Ablauf von 'splashDuration' selbst weiter.
 * - Kundenseite (pageUrl gesetzt, ZEN-29183): die vom Kunden bereitgestellte,
 *   im Admin hochgeladene HTML-Seite wird formatfuellend angezeigt. Weiter geht es
 *   erst, wenn der Benutzer den Splash explizit schliesst.
 *
 * @param {string} pageUrl URL der Kundenseite ('' = klassischer Splash)
 * @param {boolean} ready true, wenn die Stammdaten geladen sind
 * @param {function} onClose wird beim expliziten Schliessen gerufen
 */
const Splash = ({ pageUrl = '', ready = false, onClose }) => {

    const { t } = useTranslation();

    const _device = useSelector((state) => device(state));
    const _properties = useSelector((state) => properties(state));

    //    const [splashImage, setSplashImage] = useState('/webstore/service/v1/masterdata/resources/image/desktop.png');
    const [splashImage, setSplashImage] = useState('');
    const [pageLoaded, setPageLoaded] = useState(false);

    useEffect(() => {
        if (_device === 'desktop') {
            setSplashImage('/webstore/service/v1/masterdata/resources/image/desktop.png');
        } else if (window.innerWidth < _properties.splashSmall) {
            setSplashImage('/webstore/service/v1/masterdata/resources/image/phone.png');
        } else if (window.innerWidth < _properties.splashMedium) {
            setSplashImage('/webstore/service/v1/masterdata/resources/image/mini.png');
        } else if (window.innerWidth < _properties.splashLarge) {
            setSplashImage('/webstore/service/v1/masterdata/resources/image/tablet.png');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [_device])

    // ZEN-29183: Kundenseite auch per Escape schliessbar
    useEffect(() => {
        if (!pageUrl || !onClose) {
            return undefined;
        }
        const keyListener = (event) => {
            if ('Escape' === event.key) {
                onClose();
            }
        };
        window.addEventListener('keydown', keyListener);
        return () => window.removeEventListener('keydown', keyListener);
    }, [pageUrl, onClose]);

    // ZEN-29183: Notausstieg, falls die Kundenseite nicht laedt
    useEffect(() => {
        if (!pageUrl || pageLoaded || !onClose) {
            return undefined;
        }
        const timer = setTimeout(() => {
            console.warn(`Splash: Kundenseite ${pageUrl} nicht geladen, Splash wird geschlossen.`);
            onClose();
        }, PAGE_LOAD_TIMEOUT);
        return () => clearTimeout(timer);
    }, [pageUrl, pageLoaded, onClose]);

    if (pageUrl) {
        return (
            <Box id="splash" sx={{ backgroundColor: _properties.splashBackground, overflow: 'hidden' }}>
                <iframe
                    id="splash-page"
                    title={t('splash.title')}
                    src={pageUrl}
                    onLoad={() => setPageLoaded(true)}
                    style={{ border: 0, display: 'block', width: '100%', height: '100%' }}>
                </iframe>
                <Box sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}>
                    <Button
                        id="splash-enter"
                        variant="contained"
                        autoFocus
                        onClick={onClose}>
                        {t('splash.enter')}
                    </Button>
                    <IconButton
                        id="splash-close"
                        aria-label={t('common.actions.close')}
                        onClick={onClose}
                        sx={{ backgroundColor: 'background.paper', '&:hover': { backgroundColor: 'background.paper' } }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                {!ready &&
                    <LinearProgress sx={{ position: 'absolute', left: 0, bottom: 0, width: '100%' }}></LinearProgress>
                }
            </Box>
        );
    }

    return (
        <Box id="splash" sx={{ backgroundImage: `url(${splashImage})`, backgroundColor: _properties.splashBackground }}>
            <LinearProgress></LinearProgress>
        </Box>
    );
}
export default Splash;
